import ExpandableDrawer from "@src/components/expandable-drawer/ExpandableDrawer";
import { Input, toast } from "top-brains-ui";
import { Formik, Form } from "formik";
import styles from "@src/pages/login/change-password/ChangePassword.module.scss";
import * as Yup from "yup";
import { useChangePasswordMutation } from "@src/api/LoginApiSlice";
import { useSelector } from "react-redux";
import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { RootState } from "@src/app/store";

const validationSchema = Yup.object().shape({
  oldPassword: Yup.string().required("Old Password is required"),
  newPassword: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("New Password is required")
    .notOneOf(
      [Yup.ref("oldPassword")],
      "New password must be different from old password"
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Confirm Password is required"),
});

interface ChangePasswordProps {
  onClose: (value: boolean) => void;
  isDrawerOpen: boolean;
}

const ChangePassword: FC<ChangePasswordProps> = ({ onClose, isDrawerOpen }) => {
  const userId = useSelector(
    (state: RootState) => state.globalSlice.userDetails?.id
  );
  const [changePassword] = useChangePasswordMutation();
  const navigate = useNavigate();

  return (
    <Formik
      initialValues={{
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        try {
          await changePassword({
            payload: {
              userId,
              oldPassword: values.oldPassword,
              newPassword: values.newPassword,
            },
          }).unwrap();
          onClose(false);
          toast.success("Success", "Password changed successfully");
          setTimeout(() => {
            navigate("/");
          }, 2000);
        } catch (err: unknown) {
          console.error(err);

          const typedError = err as {
            data: {
              httpStatus: string;
              response: string;
              statusCode: number;
            };
          };

          toast.error(
            "Error",
            typedError.data?.response ?? "Something went wrong"
          );
        }
      }}
    >
      {({
        values,
        handleChange,
        handleSubmit,
        setFieldTouched,
        errors,
        touched,
        isValid,
        dirty,
      }) => (
        <ExpandableDrawer
          headerTitle="Change Password"
          showOverlay={true}
          isOpen={isDrawerOpen}
          showCloseIcon={true}
          onClose={() => {
            onClose(false);
          }}
          size="small"
          zIndex={1001}
          footerLeftContent={<div></div>}
          buttons={[
            {
              label: "Cancel",
              variant: "secondary",
              type: "button",
              onClick: () => {
                onClose(false);
              },
            },
            {
              label: "Confirm",
              variant: "primary",
              type: "submit",
              onClick: handleSubmit,
              disable: !isValid || !dirty,
            },
          ]}
        >
          <Form className={styles.passwordForm}>
            <Input
              name="oldPassword"
              label="Old Password"
              required
              type="password"
              value={values.oldPassword}
              onChange={(e) => {
                handleChange(e);
                setFieldTouched("oldPassword", true, false);
              }}
              onBlur={() => {
                setFieldTouched("oldPassword", true, true);
              }}
              error={Boolean(touched.oldPassword && errors.oldPassword)}
              helperText={
                touched.oldPassword && errors.oldPassword
                  ? errors.oldPassword
                  : undefined
              }
            />
            <Input
              name="newPassword"
              label="New Password"
              type="password"
              required
              value={values.newPassword}
              onChange={(e) => {
                handleChange(e);
                setFieldTouched("newPassword", true, false);
              }}
              onBlur={() => {
                setFieldTouched("newPassword", true, true);
              }}
              error={Boolean(touched.newPassword && errors.newPassword)}
              helperText={
                touched.newPassword && errors.newPassword
                  ? errors.newPassword
                  : undefined
              }
            />

            <Input
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              required
              value={values.confirmPassword}
              onChange={(e) => {
                handleChange(e);
                setFieldTouched("confirmPassword", true, false);
              }}
              onBlur={() => {
                setFieldTouched("confirmPassword", true, true);
              }}
              error={Boolean(touched.confirmPassword && errors.confirmPassword)}
              helperText={
                touched.confirmPassword && errors.confirmPassword
                  ? errors.confirmPassword
                  : undefined
              }
            />
          </Form>
        </ExpandableDrawer>
      )}
    </Formik>
  );
};

export default ChangePassword;
